import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { PerfilService } from '../services/perfil.service';
import * as PerfilActions from './perfil.actions';

@Injectable()
export class PerfilEffects {
  private readonly actions$ = inject(Actions);
  private readonly perfilService = inject(PerfilService);
  private readonly store = inject(Store);

  // Load Perfiles
  loadPerfiles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PerfilActions.loadPerfiles),
      switchMap(() =>
        this.perfilService.getPerfiles().pipe(
          map(perfiles => PerfilActions.loadPerfilesSuccess({ perfiles })),
          catchError(error => {
            return of(
              PerfilActions.loadPerfilesFailure({
                error: error.message || 'Error al cargar los perfiles'
              })
            );
          })
        )
      )
    )
  );

  // Create Perfil
  createPerfil$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PerfilActions.createPerfil),
      switchMap(action =>
        this.perfilService.createPerfil(action.perfil).pipe(
          map(createdPerfil =>
            PerfilActions.createPerfilSuccess({
              perfil: createdPerfil
            })
          ),
          catchError(error => {
            return of(
              PerfilActions.createPerfilFailure({
                error: error.message || 'Error al crear el perfil'
              })
            );
          })
        )
      )
    )
  );

  // Update Perfil
  updatePerfil$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PerfilActions.updatePerfil),
      switchMap(action =>
        this.perfilService.updatePerfil(action.id, action.perfil).pipe(
          switchMap(() =>
            this.perfilService.getPerfilById(action.id).pipe(
              map(updatedPerfil =>
                PerfilActions.updatePerfilSuccess({
                  perfil: updatedPerfil
                })
              )
            )
          ),
          catchError(error => {
            return of(
              PerfilActions.updatePerfilFailure({
                error: error.message || 'Error al actualizar el perfil'
              })
            );
          })
        )
      )
    )
  );

  // Delete Perfil
  deletePerfil$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PerfilActions.deletePerfil),
      switchMap(action =>
        this.perfilService.deletePerfil(action.id).pipe(
          map(() => PerfilActions.deletePerfilSuccess({ id: action.id })),
          catchError((error: unknown) => {
            let message = 'Error al eliminar el perfil';
            if (error && typeof error === 'object') {
              const httpStatus = (error as { status?: number }).status;
              const backendMessage = (error as { error?: { message?: string } }).error?.message;
              if (httpStatus === 409 && backendMessage) {
                message = backendMessage;
              }
            }

            return of(PerfilActions.deletePerfilFailure({ error: message }));
          })
        )
      )
    )
  );

  // Search Perfiles
  searchPerfiles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PerfilActions.searchPerfiles),
      switchMap(() =>
        of([]).pipe(
          map(results => PerfilActions.searchPerfilesSuccess({ results })),
          catchError(error =>
            of(
              PerfilActions.loadPerfilesFailure({
                error: error.message || 'Error al buscar perfiles'
              })
            )
          )
        )
      )
    )
  );
}
